/**
 * Wissen (knowledge_chunks): Freitext-Bausteine (FAQ/Leistungen), die der oeffentliche KI-Chat
 * (RAG) als Quelle nutzt. Liste + Anlegen/Bearbeiten-Drawer + Loeschen. Alles gegen die Live-API.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";
import { Drawer } from "../ui/components";

interface KnowledgeChunk {
  id: string;
  title: string;
  content: string;
  tags: string | null;
  active: boolean;
  updatedAt: string;
}

const EMPTY = { title: "", content: "", tags: "", active: true };
type Form = typeof EMPTY;

export default function KnowledgePage() {
  const { data, loading, error, reload } = useApi<KnowledgeChunk[]>("/api/knowledge");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const chunks = data ?? [];

  // A-06: Hilfe-Panel und Topbar-Suche navigieren mit ?q=... hierher. Diese Seite wertet den
  // Parameter jetzt aus (initial + reaktiv) und filtert die Bausteine nach Titel/Inhalt/Tags.
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => {
    const q = params.get("q");
    if (q != null) setSearch(q);
  }, [params]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chunks;
    return chunks.filter(
      (k) =>
        k.title.toLowerCase().includes(q) ||
        k.content.toLowerCase().includes(q) ||
        (k.tags ?? "").toLowerCase().includes(q),
    );
  }, [chunks, search]);

  function openNew() {
    setEditId(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  function openEdit(k: KnowledgeChunk) {
    setEditId(k.id);
    setForm({ title: k.title, content: k.content, tags: k.tags ?? "", active: k.active });
    setFormError(null);
    setOpen(true);
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const body = { title: form.title, content: form.content, tags: form.tags || null, active: form.active };
      if (editId) await apiMutate(`/api/knowledge/${editId}`, "PATCH", body);
      else await apiMutate("/api/knowledge", "POST", body);
      setOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate(`/api/knowledge/${id}`, "DELETE");
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Wissen"
        subtitle="Bausteine für den KI-Chat: FAQ, Leistungen, Ablauf. Je konkreter, desto besser antwortet die KI."
        actions={
          <>
            {/* CX-Audit 2026-07-17: Die Seite pflegt den KI-Chat, bot aber keinen Weg, das
                Ergebnis zu SEHEN — „Chat testen" öffnet die öffentliche Chat-Seite im neuen Tab. */}
            <a href="/chat" target="_blank" rel="noreferrer" className="btn-secondary">
              <Icon name="open_in_new" className="text-[18px]" /> Chat testen
            </a>
            <button type="button" className="btn-primary" onClick={openNew}>
              <Icon name="add" className="text-[18px]" /> Baustein hinzufügen
            </button>
          </>
        }
      />

      {error && <ErrorNote message={error} />}
      {formError && <ErrorNote message={formError} />}

      {chunks.length > 0 && (
        <label className="relative mb-3 block">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant" />
          <input
            className="input pl-9"
            placeholder="Bausteine durchsuchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <LoadingRows rows={3} />
        ) : chunks.length === 0 ? (
          <EmptyState icon="menu_book" title="Noch kein Wissen hinterlegt" hint='Legen Sie oben rechts den ersten Baustein an (z. B. „Anfahrt & Liefergebiet").' />
        ) : filtered.length === 0 ? (
          <EmptyState icon="search_off" title="Kein Baustein passt zur Suche" hint={`Keine Treffer für „${search.trim()}".`} />
        ) : (
          <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
            {filtered.map((k) => (
              <li key={k.id} className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{k.title}</p>
                    {!k.active && <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-label-md text-secondary dark:bg-surface-variant/40">inaktiv</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 font-body-sm text-body-sm text-on-surface-variant">{k.content}</p>
                  {k.tags && <p className="mt-1 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{k.tags}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline" onClick={() => openEdit(k)}>
                    <Icon name="edit" className="text-[16px]" /> Bearbeiten
                  </button>
                  <button type="button" className="inline-flex items-center gap-1 font-label-md text-label-md text-error hover:underline disabled:opacity-50" disabled={busy} onClick={() => void remove(k.id)}>
                    <Icon name="delete" className="text-[16px]" /> Löschen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editId ? "Baustein bearbeiten" : "Baustein hinzufügen"}>
        <form className="space-y-4" onSubmit={(e) => void save(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Titel *</span>
            <input required className="input" placeholder="z. B. Anfahrt & Liefergebiet" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Inhalt *</span>
            <textarea required rows={8} className="input" placeholder="Klartext, den die KI dem Gast erklären darf …" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Tags</span>
            <input className="input" placeholder="anfahrt, lieferung, preise" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} />
          </label>
          <label className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv (wird vom KI-Chat genutzt)
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
